import { animate, state, style, transition, trigger } from '@angular/animations';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { filter, withLatestFrom } from 'rxjs/operators';
import { IdentityService } from 'src/app/core/identity.service';
import { PerDiemRecruitingService } from 'src/app/perdiem-recruiting/perdiem-recruiting.service';
import { RecruitingService } from 'src/app/recruiting/recruiting.service';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { CalendarComponent } from 'src/app/shared/components/calendar/calendar.component';
import { FollowupPickerProfileBaseComponent } from 'src/app/shared/components/followup-picker/followup-picker-profile-base.component';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { PerDiemFollowupView } from 'src/app/shared/models/perdiem-followup-view.model';
import {
    selectConfirmationDialogData,
    selectConfirmationDialogResult
} from 'src/app/shared/selectors/confirmation-dialog.selectors';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { MatChipsModule } from '@angular/material/chips';

@UntilDestroy()
@Component({
    selector: 'nova-followup-picker-perdiem',
    templateUrl: './followup-picker-perdiem.component.html',
    styleUrls: ['./followup-picker-perdiem.component.scss'],
    animations: [
        trigger('balloonEffect', [
            state(
                'initial',
                style({
                    opacity: 0
                })
            ),
            state(
                'final',
                style({
                    opacity: 1
                })
            ),
            transition('initial=>final', animate('1500ms')),
            transition('final=>initial', animate('1500ms'))
        ])
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [provideNgxMask()],
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatChipsModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        NgxMaskDirective,
        CalendarComponent,
        NgClass,
        MatTooltipModule
    ]
})
export class FollowupPickerPerdiemComponent extends FollowupPickerProfileBaseComponent {
    @Input() currentFollowup: PerDiemFollowupView;
    readonly featureFlag = FeatureFlag;
    constructor(
        private readonly perDiemRecruitingService: PerDiemRecruitingService,
        private readonly _recruitingService: RecruitingService,
        private readonly _identityService: IdentityService,
        private readonly storeState: Store,
        protected readonly _featureManager: LDFeatureManager
    ) {
        super(_recruitingService, _identityService, storeState, _featureManager);
    }

    protected initLookups(shouldSkipGetFollowupSummary: boolean) {
        this.getLookups(this.currentDate, this.perDiemRecruitingService, shouldSkipGetFollowupSummary);
    }

    protected selectedDateChanged(newDate: Date): void {
        this.handleSelectedDateChanged(newDate, this.perDiemRecruitingService);
    }

    patchData(followUp: PerDiemFollowupView): void {
        if (followUp && this.resourcesForm) {
            this.followUpId = followUp?.id;
            const priorIndex = this.priorityList.findIndex((e) => e.type === followUp.priority);
            const followUpDate = new Date(followUp.followupDate);
            const timeString = followUpDate.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
            });
            const timeIndex =
                followUp.followupDate === null ? 0 : this.hours.findIndex((e) => e.followupTime === timeString);
            this.priority = this.priorityList[priorIndex];
            this.hour = this.hours[timeIndex];
            this.patchDataToForm(followUp, priorIndex, timeIndex);
        }
    }

    saveFollowUp(markComplete = false): void {
        if (!this.saveInProgress && !this.changeInProgress && this.resourcesForm?.value?.date) {
            this.saveInProgress = true;
            const follow = this.createDto();
            this.updateRecruiterResources(this.perDiemRecruitingService, follow, markComplete);
        }
    }

    protected createDto(): PerDiemFollowupView {
        return this.createPerDiemFollowUpDto(this.followUpId);
    }

    isMarkedComplete(): boolean {
        return !this.currentFollowup?.followupDate;
    }

    handleMarkAsCompleteConfirmationResult(): void {
        this.storeState
            .select(selectConfirmationDialogResult)
            .pipe(
                filter((result) => result),
                withLatestFrom(this.storeState.select(selectConfirmationDialogData)),
                untilDestroyed(this)
            )
            .subscribe((confirmed) => {
                if (confirmed) {
                    this.requestMarkAsComplete(this.perDiemRecruitingService, this.currentFollowup);
                }
            });
    }

    protected markAsComplete(): void {
        this.storeState.dispatch(
            showToasterMessage({
                message: '1 followup has been completed.'
            })
        );
        this.resetDefaultValues();
        this.resourcesForm.markAsPristine();
        this.saveInProgress = false;
        this.followUpId = null;
        this.currentFollowup = {
            id: null,
            candidateId: this.currentFollowup.candidateId,
            priority: 1,
            followupDate: null,
            recruiterId: this.recruiterId,
            followupReason: ''
        };
        this.onChangeFollowup.emit([this.currentFollowup]);
    }
}
